import ScreenKeys from '@/Enums/Screens/ScreenKey.enum';
import ModalCard from '@/components/Modal/common/ModalCard';
import { isSuperAdminRole } from '@/helpers/roles';
import { FormGroup } from '@mui/material';
import PropTypes from 'prop-types';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import AccessManagementLoadingSkeleton from './AccessManagementLoadingSkeleton';
import ReportingV2SubToggles, { isReportingV2SubTogglesVisible } from './ReportingV2SubToggles';
import TripleToggleControl from './TripleToggleControl';

const DEFAULT_COLUMN_SIZE = 4;
const MAX_COLUMNS = 3;
const getControlColumns = (controls = []) => {
  const chunks = [];
  const columnSize = Math.max(DEFAULT_COLUMN_SIZE, Math.ceil(controls.length / MAX_COLUMNS));
  for (let i = 0; i < controls.length; i += columnSize) {
    const chunk = controls.slice(i, i + columnSize);
    chunks.push(chunk);
  }
  return chunks;
};

const AccessManagement = ({ userRole, meRole }) => {
  const { screensByRole, screensByRoleRequestId } = useSelector(state => state.userManagement);

  const accessManagementScreens = screensByRole[+userRole];
  // Only a Ribeye admin may hold Unified Reporting and the legacy dashboard at
  // once (#4245) — partner-side admins stay one-or-the-other.
  const allowBoth = isSuperAdminRole(userRole);
  const columns = getControlColumns(accessManagementScreens);

  const {
    formState: { errors },
    control,
  } = useFormContext();

  // Per-feature overrides (flank#4442/#4446/#4644/#4648) nested under Unified
  // Reporting — only shown once that screen is actually granted, and only to
  // a Ribeye/Super Admin VIEWER (meRole), never based on the target's own
  // role (live product review, 2026-10-09: an Org/Whitelabel Admin editing
  // another Org/Whitelabel Admin must not see this area at all, though they
  // still see and can edit the main Unified Reporting toggle above it).
  // Watches the whole `accessManagement` object rather than the REPORTING_V2
  // leaf path: a leaf-path useWatch can miss the bulk reset() that populates
  // an existing user's data, so the panel never appeared until the admin
  // re-toggled the screen by hand. Watching the parent object picks up
  // reset() correctly.
  const accessManagementValues = useWatch({ control, name: 'accessManagement' });
  const reportingV2Access = accessManagementValues?.[ScreenKeys.REPORTING_V2];
  const canEditSubToggles = isSuperAdminRole(meRole);
  const showReportingV2SubToggles = isReportingV2SubTogglesVisible(meRole, reportingV2Access);

  const error = errors.accessManagement ? Object.values(errors.accessManagement)[0] : null;

  return (
    <>
      <div className='mt-6 flex justify-between'>
        <div className='mb-3'>
          <div className='font-semibold text-[18px]'>Access Management</div>
        </div>
      </div>
      <ModalCard
        sx={{
          borderRadius: '8px',
          padding: '18px 24px',
        }}
      >
        <div className='flex gap-[8px]'>
          {screensByRoleRequestId ? (
            <AccessManagementLoadingSkeleton />
          ) : (
            columns.map((column, col) => (
              <FormGroup key={col}>
                {column.map(screen => (
                  <div key={screen.value}>
                    {/* flank#4749/#4750: one control style for every user type — Off/Read/Full
                      for everyone, not a Checkbox for Admin-tier targets and a TripleToggle for
                      everyone else. Checkbox was a strict subset of this (true/false only, no Read),
                      so unifying here adds Read-level granularity for Admin-tier targets but drops
                      nothing — SCREENS_WITH_READ_ONLY_ACCESS_OPTION still disables Read per-screen
                      exactly as it already did for Client-tier targets. */}
                    <TripleToggleControl {...screen} control={control} allowBoth={allowBoth} />
                    {screen.value === ScreenKeys.REPORTING_V2 && showReportingV2SubToggles && (
                      <ReportingV2SubToggles control={control} canEdit={canEditSubToggles} userRole={userRole} />
                    )}
                  </div>
                ))}
              </FormGroup>
            ))
          )}
        </div>
        {error ? <span className='text-[#d32f2f] text-[12px]'>{error?.message}</span> : null}
      </ModalCard>
    </>
  );
};

AccessManagement.propTypes = {
  userRole: PropTypes.number,
  meRole: PropTypes.number,
};

export default AccessManagement;
